import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for fetching ALL role permissions
export const fetchAllRolePermissions = createAsyncThunk(
  "rolePermission/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const requestBody = {
        id: 0,
        compId: 0,
        branId: 0,
        userId: 0,
        searchText: "",
        fromDate: new Date().toISOString(),
        toDate: new Date().toISOString(),
        status: "",
        type: "",
        pageNumber: 1,
        pageSize: 10000,
        totalRecords: 0,
      };

      const response = await axiosInstance.get(
        API_ENDPOINTS.ROLE_PERMISSION.SELECT_ALL,
        { params: requestBody },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch role permissions",
      );
    }
  },
);

// Thunk for fetching role permissions by role ID
export const fetchRolePermissionsByRoleId = createAsyncThunk(
  "rolePermission/fetchByRoleId",
  async (roleId, { rejectWithValue }) => {
    try {
      const requestBody = {
        id: roleId,
        compId: 0,
        branId: 0,
        userId: 0,
        roleId: roleId,
        searchText: "",
        fromDate: new Date().toISOString(),
        toDate: new Date().toISOString(),
        status: "",
        type: "",
        pageNumber: 1,
        pageSize: 10000,
        totalRecords: 0,
      };

      // Most standard QMS endpoints use SelectAll to filter by roleId or get all records
      const response = await axiosInstance.get(
        API_ENDPOINTS.ROLE_PERMISSION.SELECT_ALL,
        { params: requestBody },
      );
      const rolePermissions =
        response.data?.data?.data || response.data?.data || response.data || [];

      // Filter if needed (backend should filter by roleId)
      const filteredPermissions = rolePermissions.filter(
        (p) => p.roleId === roleId || p.RoleId === roleId,
      );

      return filteredPermissions;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Role permissions not found",
      );
    }
  },
);

// Thunk for saving role permissions (using new bulk save API)
export const saveRolePermissions = createAsyncThunk(
  "rolePermission/save",
  async (permissionsData, { rejectWithValue }) => {
    try {
      // Format payload according to the API requirements
      const roleId = permissionsData.length > 0 ? permissionsData[0].roleId : 0;
      const formattedPayload = {
        roleId: roleId,
        permissions: permissionsData.map((p) => ({
          rolePermissionId: p.rolePermissionId || 0,
          roleId: p.roleId || roleId,
          moduleId: p.moduleId,
          canView: p.canView,
          canAdd: p.canAdd,
          canEdit: p.canEdit,
          canDelete: p.canDelete,
        })),
      };

      await axiosInstance.post(
        API_ENDPOINTS.ROLE_PERMISSION.SAVE,
        formattedPayload,
      );

      const savedPermissions = permissionsData.map((p) => ({
        rolePermissionId: p.rolePermissionId,
        roleId: p.roleId,
        moduleId: p.moduleId,
        canView: p.canView,
        canAdd: p.canAdd,
        canEdit: p.canEdit,
        canDelete: p.canDelete,
      }));

      return { data: savedPermissions };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to save role permissions",
      );
    }
  },
);

// Thunk for updating a single role permission
export const updateRolePermission = createAsyncThunk(
  "rolePermission/update",
  async (permissionData, { rejectWithValue, dispatch }) => {
    try {
      const payload = {
        rolePermissionId: permissionData.rolePermissionId || 0,
        roleId: permissionData.roleId,
        moduleId: permissionData.moduleId,
        canView: permissionData.canView,
        canAdd: permissionData.canAdd,
        canEdit: permissionData.canEdit,
        canDelete: permissionData.canDelete,
      };
      const response = await axiosInstance.put(
        API_ENDPOINTS.ROLE_PERMISSION.UPDATE,
        payload,
      );
      dispatch(fetchAllRolePermissions());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update role permission",
      );
    }
  },
);

// Thunk for deleting role permission
export const deleteRolePermission = createAsyncThunk(
  "rolePermission/delete",
  async (permissionId, { rejectWithValue, dispatch }) => {
    try {
      await axiosInstance.delete(
        `${API_ENDPOINTS.ROLE_PERMISSION.DELETE}?id=${permissionId}`,
      );
      dispatch(fetchAllRolePermissions());
      return permissionId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete role permission",
      );
    }
  },
);

const rolePermissionSlice = createSlice({
  name: "rolePermission",
  initialState: {
    permissionsList: [],
    selectedPermissions: null,
    isLoading: false,
    error: null,
    success: false,
  },
  reducers: {
    clearSelectedPermissions: (state) => {
      state.selectedPermissions = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Permissions
      .addCase(fetchAllRolePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllRolePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissionsList = action.payload.data || [];
      })
      .addCase(fetchAllRolePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch By Role ID
      .addCase(fetchRolePermissionsByRoleId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRolePermissionsByRoleId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedPermissions = action.payload || [];
      })
      .addCase(fetchRolePermissionsByRoleId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Save Permissions - optimistic update
      .addCase(saveRolePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.selectedPermissions = action.payload.data || action.payload || [];
      })
      // Save Permissions
      .addCase(saveRolePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })

      .addCase(saveRolePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Update Permission
      .addCase(updateRolePermission.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRolePermission.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateRolePermission.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Permission
      .addCase(deleteRolePermission.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRolePermission.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteRolePermission.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedPermissions, clearSuccess } =
  rolePermissionSlice.actions;

export const selectRolePermissionsList = (state) =>
  state.rolePermission.permissionsList || [];

export const selectRolePermissionLoading = (state) =>
  state.rolePermission.isLoading;

export default rolePermissionSlice.reducer;
